import { ReactNode, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { Download, Lock, PanelLeftClose, X } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { usePlanFeatures } from "@/hooks/usePlanFeatures";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import { cn } from "@/lib/utils";
import { ROLE_NAV, SHELL_FOOTER_NAV, SHELL_NAV, isNavItemActive, type ShellNavItem } from "./navigation";
import { SidebarShareJoylo } from "./SidebarShareJoylo";
import { SidebarCredits } from "./SidebarCredits";
import { SidebarWorkspaceSwitch } from "./SidebarWorkspaceSwitch";

const downloadLogo = (ext: string) => {
  const link = document.createElement("a");
  link.href = `/logo.${ext}`;
  link.download = `logo.${ext}`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

type ShellUser = { roles?: { name?: string; display_name?: string }[]; is_influencer?: boolean } | null | undefined;

const hasRole = (user: ShellUser, needle: string) =>
  Boolean(
    user?.roles?.some(
      (role) => role.name?.toLowerCase().includes(needle) || role.display_name?.toLowerCase().includes(needle),
    ),
  );

/** Nav groups (team-only items filtered) and role links for the current user and workspace. */
export const useShellNavItems = () => {
  const user = useAuth().user as ShellUser;
  const { activeWorkspace } = useWorkspace();
  const isTeam = activeWorkspace?.type === "team";

  const roleItems = useMemo(() => {
    const items: ShellNavItem[] = [];
    if (hasRole(user, "admin")) items.push(ROLE_NAV.admin);
    if (hasRole(user, "developer")) items.push(ROLE_NAV.developer);
    if (user?.is_influencer) items.push(ROLE_NAV.influencer);
    return items;
  }, [user]);

  const groups = useMemo(
    () =>
      SHELL_NAV.map((group) => ({
        ...group,
        items: group.items.filter((item) => !item.teamOnly || isTeam),
      })),
    [isTeam],
  );

  return { groups, roleItems };
};

interface ShellSidebarBodyProps {
  // Router state carried on every link, so settings opened over the editor stay an overlay
  linkState?: unknown;
  onClose: () => void;
  // "drawer" shows an X (mobile shell, project page on phones); "rail" shows the collapse icon; "none" when the page has its own toggle
  closeVariant: "drawer" | "rail" | "none";
}

// Sidebar contents shared by the app shell and the project page's navigation drawer
export const ShellSidebarBody = ({ linkState, onClose, closeVariant }: ShellSidebarBodyProps) => {
  const location = useLocation();
  const { isRestricted } = usePlanFeatures();
  const { groups, roleItems } = useShellNavItems();

  const NavItem = ({ item }: { item: ShellNavItem }) => {
    const active = isNavItemActive(item, location.pathname);
    const locked = item.gate ? isRestricted(item.gate) : false;
    return (
      <Link
        to={item.to}
        state={linkState}
        aria-current={active ? "page" : undefined}
        onClick={(e) => {
          if (locked) e.preventDefault();
        }}
        className={cn(
          "flex min-h-[40px] items-center gap-[11px] rounded-lg px-3 py-2.5 text-[13px] font-medium transition-colors",
          active
            ? "bg-selected text-plum shadow-[inset_2px_0_hsl(var(--plum))]"
            : "text-muted-foreground hover:bg-soft hover:text-foreground",
          locked && "opacity-60 cursor-not-allowed",
        )}
      >
        <item.icon className="w-4 h-4 shrink-0" />
        <span className="truncate">{item.label}</span>
        {locked && <Lock className="ml-auto w-3 h-3" aria-label="Upgrade required" />}
      </Link>
    );
  };

  const GroupLabel = ({ children: label, first = false }: { children: ReactNode; first?: boolean }) => (
    <div className={cn("px-3 mb-1.5 text-[10px] font-semibold uppercase tracking-[0.13em] text-muted-foreground", first ? "mt-1" : "mt-4")}>
      {label}
    </div>
  );

  return (
    <>
      <div className="flex items-center justify-between px-3.5 pb-3.5">
        <ContextMenu>
          <ContextMenuTrigger asChild>
            <Link to="/~" aria-label="Joylo workspace home" state={linkState}>
              <img src="/logo.svg" alt="Joylo" className="h-9 w-auto dark:hidden" />
              <img src="/logo-dark.svg" alt="Joylo" className="hidden h-9 w-auto dark:block" />
            </Link>
          </ContextMenuTrigger>
          <ContextMenuContent className="z-[10030] w-56">
            {["svg", "png"].map((ext) => (
              <ContextMenuItem key={ext} onClick={() => downloadLogo(ext)} className="cursor-pointer">
                <Download className="w-4 h-4 mr-2" />
                Download {ext.toUpperCase()}
              </ContextMenuItem>
            ))}
          </ContextMenuContent>
        </ContextMenu>
        {closeVariant !== "none" && (
          <button
            className="grid h-9 w-9 place-items-center rounded-lg text-muted-foreground hover:bg-soft hover:text-foreground"
            aria-label={closeVariant === "drawer" ? "Close navigation" : "Hide navigation"}
            onClick={onClose}
          >
            {closeVariant === "drawer" ? <X className="w-[18px] h-[18px]" /> : <PanelLeftClose className="w-[18px] h-[18px]" />}
          </button>
        )}
      </div>

      <SidebarWorkspaceSwitch linkState={linkState} />

      <div className="joylo-scroll -mx-1 min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-1">
        {groups.map((group, i) => (
          <div key={group.label}>
            <GroupLabel first={i === 0}>{group.label}</GroupLabel>
            <nav className="grid gap-[3px]" aria-label={group.label}>
              {group.items.map((item) => (
                <NavItem key={item.to} item={item} />
              ))}
            </nav>
          </div>
        ))}
        {roleItems.length > 0 && (
          <div>
            <GroupLabel>Roles</GroupLabel>
            <nav className="grid gap-[3px]" aria-label="Roles">
              {roleItems.map((item) => (
                <NavItem key={item.to} item={item} />
              ))}
            </nav>
          </div>
        )}
      </div>

      <div className="shrink-0 pt-3">
        <SidebarCredits linkState={linkState} />
        {SHELL_FOOTER_NAV.filter((item) => item.to !== "/referrals").map((item) => (
          <NavItem key={item.to} item={item} />
        ))}
        <SidebarShareJoylo linkState={linkState} />
      </div>
    </>
  );
};
